import { useOptimizationGroupDataUpdate, useSnackbar, useTokenInfo } from '@e2m-customer-portal/hooks';
import {
  mustBeGreaterThan,
  mustBeLessThan,
  Nullable,
  optimizationWhitelist,
  UserRole,
} from '@e2m-customer-portal/shared';
import {
  Drawer,
  DrawerButtons,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  InfoTooltip,
  NumberInput,
  ValidatedContainer,
} from '@e2m-customer-portal/ui';
import { faker } from '@faker-js/faker';
import { Button } from '@mui/material';

import { format } from 'date-fns';
import { AutomatedParameterInput } from 'domains/optimization-group/items/AutomatedParameterInput';
import { PeakWindow } from 'domains/optimization-group/items/PeakWindow';
import {
  AssetType,
  AutomatedParameter,
  BatteryDataType,
  OptimizationParameterValue,
  PeakWindowType,
  Season,
} from 'domains/optimization-group/types';
import { equals } from 'ramda';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BatterySidePanelProps {
  data: BatteryDataType;
  identifier?: string;
  id: string;
  assetType: AssetType;
  open: boolean;
  onClose: () => void;
}

export function BatterySidePanel({ data, identifier, id, assetType, open, onClose }: Readonly<BatterySidePanelProps>) {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const { roles } = useTokenInfo();
  const isVPPOperator = roles.has(UserRole.VPPOperator);
  const [isValid, setIsValid] = useState(false);
  const { showError, showSuccess } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [update] = useOptimizationGroupDataUpdate();

  const [updatedData, setUpdatedData] = useState<BatteryDataType>({ ...data });
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [peakWindows, setPeakWindows] = useState<PeakWindowType[] | undefined>(data.peak_windows);

  const readOnly = (parameter: string) => !isVPPOperator && !!parameter && !optimizationWhitelist[parameter];

  const updateValues = () => {
    setIsSaving(true);
    if (isValid) {
      const mutationBody = identifier
        ? JSON.stringify({
            asset_type: assetType,
            batteries: { [identifier]: Object.fromEntries(changes) },
          })
        : JSON.stringify({
            asset_type: assetType,
            battery: Object.fromEntries(changes),
          });

      update(id, mutationBody)
        .then((res) => {
          if (res.data?.OptimizationGroupDataUpdate.json) {
            showSuccess(t('COPT_EDIT_SUCCESS_MESSAGE'));
          }
        })
        .catch((error) => {
          showError(error);
        })
        .finally(() => {
          setIsSaving(false);
          setChanges(new Map());
          onClose();
        });
    }
  };

  const resetValues = () => {
    setUpdatedData({ ...data });
    setPeakWindows(data?.peak_windows);
    setChanges(new Map());
  };

  useEffect(() => {
    if (open) {
      setUpdatedData({ ...data });
    }
  }, [open]);

  useEffect(() => {
    if (peakWindows && !equals(peakWindows, data?.peak_windows)) {
      setChanges(changes.set('peak_windows', peakWindows));
    }
  }, [peakWindows]);

  return (
    <Drawer open={open} onClose={onClose} testId="battery-side-panel" disableEnforceFocus>
      <DrawerHeader cancelLabel={t('SHARED_CANCEL')} noDivider></DrawerHeader>
      <DrawerContent className="pt-2 *:pt-4" unknownErrorLabel={t('SHARED_ERROR_UNKNOWN')}>
        <ValidatedContainer validate="on" onValidated={setIsValid}>
          <div className="h-fit grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4">
            <h3 className="m-0 flex flex-col whitespace-nowrap xl:col-span-2">
              <span className="text-xs font-normal text-grey-300">{t('COPT_BATTERIES_TITLE')}</span>
              <span className="inline-flex items-center pt-1 text-lg font-medium">
                {updatedData.alias}
                <InfoTooltip text={t('COPT_BATTERIES_ALIAS_TOOLTIP')} field="alias" />
              </span>
            </h3>
            <ValidatedContainer validate="on" onValidated={setIsValid}>
              {(updatedData.power_charge !== undefined ||
                updatedData.power_discharge !== undefined ||
                updatedData.use_dynamic_power_limits !== undefined) && (
                <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
                  {t('COPT_BATTERIES_POWER_TITLE')}
                </h4>
              )}
              {updatedData.power_charge !== undefined && (
                <NumberInput
                  value={updatedData.power_charge === null ? null : Math.round(updatedData.power_charge * 1000)}
                  required
                  name="power_charge"
                  label={t('COPT_BATTERIES_POWER_CHARGE')}
                  onChange={(value: number) => {
                    const power_charge = value != null && !Number.isNaN(value) ? value / 1000 : null;
                    setUpdatedData({
                      ...updatedData,
                      power_charge,
                    });
                    setChanges(changes.set('power_charge', power_charge));
                  }}
                  disabled={readOnly('power_charge')}
                  notNegative
                  endAdornment={<InfoTooltip field="power_charge" objectType="batteries" />}
                />
              )}
              {updatedData.power_discharge !== undefined && (
                <NumberInput
                  value={updatedData.power_discharge === null ? null : Math.round(updatedData.power_discharge * 1000)}
                  required
                  name="power_discharge"
                  label={t('COPT_BATTERIES_POWER_DISCHARGE')}
                  onChange={(value: number) => {
                    const power_discharge = value != null && !Number.isNaN(value) ? value / 1000 : null;
                    setUpdatedData({
                      ...updatedData,
                      power_discharge,
                    });
                    setChanges(changes.set('power_discharge', power_discharge));
                  }}
                  disabled={readOnly('power_discharge')}
                  notNegative
                  endAdornment={<InfoTooltip field="power_discharge" objectType="batteries" />}
                />
              )}
              {updatedData.use_dynamic_power_limits !== undefined && (
                <label className="text-sm font-light mr-4 flex items-center xl:col-span-2">
                  <input
                    type="checkbox"
                    checked={!!updatedData.use_dynamic_power_limits}
                    disabled={readOnly('use_dynamic_power_limits')}
                    onChange={() => {
                      const use_dynamic_power_limits = !updatedData.use_dynamic_power_limits;
                      setUpdatedData({ ...updatedData, use_dynamic_power_limits });
                      setChanges(changes.set('use_dynamic_power_limits', use_dynamic_power_limits));
                    }}
                    className="w-4 h-4 mr-2 accent-green-200"
                  />
                  {t('COPT_BATTERIES_USE_DYNAMIC_POWER_LIMITS')}
                  <InfoTooltip field="use_dynamic_power_limits" objectType="batteries" />
                </label>
              )}
              {(updatedData.energy_capacity !== undefined || updatedData.nominal_capacity) && (
                <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
                  {t('COPT_BATTERIES_ENERGY_CAPACITY_TITLE')}
                </h4>
              )}
              {updatedData.energy_capacity !== undefined && (
                <NumberInput
                  value={updatedData.energy_capacity === null ? null : Math.round(updatedData.energy_capacity * 1000)}
                  name="energy_capacity"
                  label={t('COPT_BATTERIES_ENERGY_CAPACITY')}
                  required
                  onChange={(value: number) => {
                    const energy_capacity = value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
                    setUpdatedData({
                      ...updatedData,
                      energy_capacity,
                    });
                    setChanges(changes.set('energy_capacity', energy_capacity));
                  }}
                  disabled={readOnly('energy_capacity')}
                  notNegative
                  endAdornment={<InfoTooltip field="energy_capacity" objectType="batteries" />}
                />
              )}
              {updatedData.nominal_capacity !== undefined && (
                <NumberInput
                  value={updatedData.nominal_capacity === null ? null : Math.round(updatedData.nominal_capacity * 1000)}
                  name="nominal_capacity"
                  label={t('COPT_BATTERIES_NOMINAL_CAPACITY')}
                  required
                  onChange={(value: number) => {
                    const nominal_capacity = value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
                    setUpdatedData({
                      ...updatedData,
                      nominal_capacity,
                    });
                    setChanges(changes.set('nominal_capacity', nominal_capacity));
                  }}
                  disabled={readOnly('nominal_capacity')}
                  notNegative
                  endAdornment={<InfoTooltip field="nominal_capacity" objectType="batteries" />}
                />
              )}
              {(updatedData.efficiency_in !== undefined ||
                updatedData.efficiency_out !== undefined ||
                updatedData.energy_efficiency_in !== undefined ||
                updatedData.energy_efficiency_out !== undefined) && (
                <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
                  {t('COPT_BATTERIES_EFFICIENCY_TITLE')}
                </h4>
              )}
              {updatedData.efficiency_in !== undefined && (
                <NumberInput
                  value={updatedData.efficiency_in === null ? null : Math.round(updatedData.efficiency_in * 100)}
                  name="efficiency_in"
                  label={t('COPT_BATTERIES_EFFICIENCY_IN')}
                  required
                  onChange={(value: number) => {
                    const efficiency_in = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      efficiency_in,
                    });
                    setChanges(changes.set('efficiency_in', efficiency_in));
                  }}
                  disabled={readOnly('efficiency_in')}
                  notNegative
                  rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
                  endAdornment={<InfoTooltip field="efficiency_in" objectType="batteries" />}
                />
              )}
              {updatedData.efficiency_out !== undefined && (
                <NumberInput
                  value={updatedData.efficiency_out === null ? null : Math.round(updatedData.efficiency_out * 100)}
                  name="efficiency_out"
                  label={t('COPT_BATTERIES_EFFICIENCY_OUT')}
                  required
                  onChange={(value: number) => {
                    const efficiency_out = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      efficiency_out,
                    });
                    setChanges(changes.set('efficiency_out', efficiency_out));
                  }}
                  disabled={readOnly('efficiency_out')}
                  notNegative
                  rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
                  endAdornment={<InfoTooltip field="efficiency_out" objectType="batteries" />}
                />
              )}
              {updatedData.energy_efficiency_in !== undefined && (
                <NumberInput
                  value={
                    updatedData.energy_efficiency_in === null
                      ? null
                      : Math.round(updatedData.energy_efficiency_in * 100)
                  }
                  name="energy_efficiency_in"
                  label={t('COPT_BATTERIES_ENERGY_EFFICIENCY_IN')}
                  required
                  onChange={(value: number) => {
                    const energy_efficiency_in = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      energy_efficiency_in,
                    });
                    setChanges(changes.set('energy_efficiency_in', energy_efficiency_in));
                  }}
                  disabled={readOnly('energy_efficiency_in')}
                  notNegative
                  rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
                  endAdornment={<InfoTooltip field="energy_efficiency_in" objectType="batteries" />}
                />
              )}
              {updatedData.energy_efficiency_out !== undefined && (
                <NumberInput
                  value={
                    updatedData.energy_efficiency_out === null
                      ? null
                      : Math.round(updatedData.energy_efficiency_out * 100)
                  }
                  name="energy_efficiency_out"
                  label={t('COPT_BATTERIES_ENERGY_EFFICIENCY_OUT')}
                  required
                  onChange={(value: number) => {
                    const energy_efficiency_out =
                      value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      energy_efficiency_out,
                    });
                    setChanges(changes.set('energy_efficiency_out', energy_efficiency_out));
                  }}
                  disabled={readOnly('energy_efficiency_out')}
                  notNegative
                  rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
                  endAdornment={<InfoTooltip field="energy_efficiency_out" objectType="batteries" />}
                />
              )}
              {updatedData.costs_per_full_cycle !== undefined && (
                <div className="flex flex-col gap-y-4 border-solid border-0 border-t border-divider xl:col-span-2">
                  <h4 className="m-0 pt-3 text-sm font-semibold">{t('COPT_BATTERIES_COSTS')}</h4>
                  <AutomatedParameterInput
                    parameter={updatedData.costs_per_full_cycle}
                    name="costs_per_full_cycle"
                    label={t('COPT_BATTERIES_COSTS_PER_FULL_CYCLE')}
                    required
                    notNegative
                    onParameterUpdate={(costs_per_full_cycle: AutomatedParameter<number>) => {
                      setUpdatedData({
                        ...updatedData,
                        costs_per_full_cycle,
                      });
                      setChanges(changes.set('costs_per_full_cycle', costs_per_full_cycle));
                    }}
                    disabled={readOnly('costs_per_full_cycle')}
                    endAdornment={<InfoTooltip field="costs_per_full_cycle" objectType="batteries" />}
                  />
                </div>
              )}
              {(updatedData.storage_max !== undefined ||
                updatedData.storage_min !== undefined ||
                updatedData.accepted_capacity_deviation !== undefined) && (
                <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
                  {t('COPT_BATTERIES_STORAGE')}
                </h4>
              )}
              {updatedData.storage_max !== undefined && (
                <NumberInput
                  value={updatedData.storage_max === null ? null : Math.round(updatedData.storage_max * 100)}
                  name="storage_max"
                  label={t('COPT_BATTERIES_STORAGE_MAX')}
                  required
                  onChange={(value: number) => {
                    const storage_max = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      storage_max,
                    });
                    setChanges(changes.set('storage_max', storage_max));
                  }}
                  disabled={readOnly('storage_max')}
                  notNegative
                  maxValue={100}
                  endAdornment={<InfoTooltip field="storage_max" objectType="batteries" />}
                />
              )}
              {updatedData.storage_min !== undefined && (
                <NumberInput
                  value={updatedData.storage_min === null ? null : Math.round(updatedData.storage_min * 100)}
                  name="storage_min"
                  label={t('COPT_BATTERIES_STORAGE_MIN')}
                  required
                  onChange={(value: number) => {
                    const storage_min = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      storage_min,
                    });
                    setChanges(changes.set('storage_min', storage_min));
                  }}
                  disabled={readOnly('storage_min')}
                  notNegative
                  endAdornment={<InfoTooltip field="storage_min" objectType="batteries" />}
                />
              )}
              {updatedData.accepted_capacity_deviation !== undefined && (
                <div className="xl:col-span-2">
                  <NumberInput
                    value={
                      updatedData.accepted_capacity_deviation === null
                        ? null
                        : Math.round(updatedData.accepted_capacity_deviation * 100)
                    }
                    name="accepted_capacity_deviation"
                    label={t('COPT_BATTERIES_ACCEPTED_CAPACITY_DEVIATION')}
                    required
                    onChange={(value: number) => {
                      const accepted_capacity_deviation =
                        value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                      setUpdatedData({
                        ...updatedData,
                        accepted_capacity_deviation,
                      });
                      setChanges(changes.set('accepted_capacity_deviation', accepted_capacity_deviation));
                    }}
                    disabled={readOnly('accepted_capacity_deviation')}
                    notNegative
                    rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
                    endAdornment={<InfoTooltip field="accepted_capacity_deviation" objectType="batteries" />}
                  />
                </div>
              )}
              {(updatedData.cycle_per_year !== undefined ||
                updatedData.cycle_per_day !== undefined ||
                updatedData.prequalified_power !== undefined) && (
                <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
                  {t('COPT_BATTERIES_CYCLE')}
                </h4>
              )}
              {updatedData.cycle_per_year !== undefined && (
                <NumberInput
                  value={updatedData.cycle_per_year}
                  name="cycle_per_year"
                  label={t('COPT_BATTERIES_CYCLE_PER_YEAR')}
                  required
                  onChange={(value: number) => {
                    const cycle_per_year = value != null && !Number.isNaN(value) ? value : null;
                    setUpdatedData({
                      ...updatedData,
                      cycle_per_year,
                    });
                    setChanges(changes.set('cycle_per_year', cycle_per_year));
                  }}
                  disabled={readOnly('cycle_per_year')}
                  minValue={50}
                  endAdornment={<InfoTooltip field="cycle_per_year" objectType="batteries" />}
                />
              )}
              {updatedData.cycle_per_day !== undefined && (
                <NumberInput
                  value={updatedData.cycle_per_day}
                  name="cycle_per_day"
                  label={t('COPT_BATTERIES_CYCLE_PER_DAY')}
                  onChange={(value: Nullable<number>) => {
                    const cycle_per_day = value != null && !Number.isNaN(value) ? value : null;
                    setUpdatedData({
                      ...updatedData,
                      cycle_per_day,
                    });
                    setChanges(changes.set('cycle_per_day', cycle_per_day));
                  }}
                  disabled={readOnly('cycle_per_day')}
                  rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                  endAdornment={<InfoTooltip field="cycle_per_day" objectType="batteries" />}
                />
              )}
              {updatedData.prequalified_power !== undefined && (
                <div className="xl:col-span-2">
                  <NumberInput
                    value={
                      updatedData.prequalified_power === null ? null : Math.round(updatedData.prequalified_power * 1000)
                    }
                    name="prequalified_power"
                    label={t('COPT_BATTERIES_PREQUALIFIED_POWER')}
                    required
                    onChange={(value: number) => {
                      const prequalified_power =
                        value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
                      setUpdatedData({
                        ...updatedData,
                        prequalified_power,
                      });
                      setChanges(changes.set('prequalified_power', prequalified_power));
                    }}
                    disabled={readOnly('prequalified_power')}
                    notNegative
                    endAdornment={<InfoTooltip field="prequalified_power" objectType="batteries" />}
                  />
                </div>
              )}
              {updatedData.fcr_storage_reserve_time !== undefined && (
                <>
                  <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
                    {t('COPT_BATTERIES_FCR_RESERVE')}
                  </h4>
                  <NumberInput
                    value={
                      updatedData.fcr_storage_reserve_time === null ? null : updatedData.fcr_storage_reserve_time * 60
                    }
                    name="fcr_storage_reserve_time"
                    label={t('COPT_BATTERIES_FCR_STORAGE_RESERVE_TIME')}
                    required
                    onChange={(value: number) => {
                      const fcr_storage_reserve_time = value != null && !Number.isNaN(value) ? value / 60 : null;
                      setUpdatedData({
                        ...updatedData,
                        fcr_storage_reserve_time,
                      });
                      setChanges(changes.set('fcr_storage_reserve_time', fcr_storage_reserve_time));
                    }}
                    disabled={readOnly('fcr_storage_reserve_time')}
                    notNegative
                    endAdornment={<InfoTooltip field="fcr_storage_reserve_time" objectType="batteries" />}
                  />
                </>
              )}
              {(updatedData.afrr_activation_reserve_time_neg !== undefined ||
                updatedData.afrr_activation_reserve_time_pos !== undefined) && (
                <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
                  {t('COPT_BATTERIES_AFRR_RESERVE')}
                </h4>
              )}
              {updatedData.afrr_activation_reserve_time_pos !== undefined && (
                <NumberInput
                  value={
                    updatedData.afrr_activation_reserve_time_pos === null
                      ? null
                      : updatedData.afrr_activation_reserve_time_pos * 60
                  }
                  name="afrr_activation_reserve_time_pos"
                  label={t('COPT_BATTERIES_AFRR_ACTIVATION_RESERVE_TIME_POS')}
                  required
                  onChange={(value: number) => {
                    const afrr_activation_reserve_time_pos = value != null && !Number.isNaN(value) ? value / 60 : null;
                    setUpdatedData({
                      ...updatedData,
                      afrr_activation_reserve_time_pos,
                    });
                    setChanges(changes.set('afrr_activation_reserve_time_pos', afrr_activation_reserve_time_pos));
                  }}
                  disabled={readOnly('afrr_activation_reserve_time_pos')}
                  notNegative
                  endAdornment={<InfoTooltip field="afrr_activation_reserve_time_pos" objectType="batteries" />}
                />
              )}
              {updatedData.afrr_activation_reserve_time_neg !== undefined && (
                <NumberInput
                  value={
                    updatedData.afrr_activation_reserve_time_neg === null
                      ? null
                      : updatedData.afrr_activation_reserve_time_neg * 60
                  }
                  name="afrr_activation_reserve_time_neg"
                  label={t('COPT_BATTERIES_AFRR_ACTIVATION_RESERVE_TIME_NEG')}
                  required
                  onChange={(value: number) => {
                    const afrr_activation_reserve_time_neg = value != null && !Number.isNaN(value) ? value / 60 : null;
                    setUpdatedData({
                      ...updatedData,
                      afrr_activation_reserve_time_neg,
                    });
                    setChanges(changes.set('afrr_activation_reserve_time_neg', afrr_activation_reserve_time_neg));
                  }}
                  disabled={readOnly('afrr_activation_reserve_time_neg')}
                  notNegative
                  endAdornment={<InfoTooltip field="afrr_activation_reserve_time_neg" objectType="batteries" />}
                />
              )}
              {peakWindows && !!peakWindows.length && (
                <div className="pt-3 flex justify-between border-solid border-0 border-t border-divider xl:col-span-2">
                  <h4 className="m-0 text-sm font-semibold">
                    {t('COPT_BATTERIES_PEAK_WINDOWS')}
                    <InfoTooltip field="peak_windows" text={t('COPT_BATTERIES_PEAK_WINDOWS_TOOLTIP')} />
                  </h4>
                  <Button
                    color="primary"
                    className="text-2xl flex h-10 w-10 min-w-10 shrink-0 grow-0 items-center justify-center rounded-full"
                    disabled={readOnly('peak_windows')}
                    onClick={() => {
                      setPeakWindows([
                        ...peakWindows,
                        {
                          season: {
                            start_date: format(new Date().toISOString(), 'yyyy-MM-dd'),
                            end_date: format(new Date().toISOString(), 'yyyy-MM-dd'),
                            name: 'autumn' as Season,
                          },
                          start_time_str: '00:00',
                          end_time_str: '00:00',
                          start_soc: 0,
                          end_soc: 0,
                          identifier: faker.string.uuid(),
                          exclude_weekends: false,
                          additional_exclude_dates: null,
                          federal_state_holidays: null,
                          exclude_last_days_of_the_year: false,
                        },
                      ]);
                    }}
                  >
                    +
                  </Button>
                </div>
              )}
              {!!peakWindows?.length &&
                peakWindows?.map((window) => (
                  <div className="xl:col-span-2" key={window.identifier}>
                    <PeakWindow
                      data={window}
                      peakWindows={peakWindows}
                      setPeakWindows={setPeakWindows}
                      setSaveBarOpen={() => false}
                      readOnly={() => readOnly('peak_windows')}
                      setIsValid={setIsValid}
                    />
                  </div>
                ))}
            </ValidatedContainer>
          </div>
        </ValidatedContainer>
      </DrawerContent>

      <DrawerFooter testId="forecast-assistant-footer">
        <DrawerButtons
          onCancel={() => {
            resetValues();
            onClose();
          }}
          onSave={updateValues}
          isLoading={isSaving}
          disableSave={!isValid || isSaving || !isVPPOperator}
          cancelLabel={t('SHARED_CANCEL')}
          saveLabel={t('SHARED_SUBMIT')}
        />
      </DrawerFooter>
    </Drawer>
  );
}
